import { canAddCommentPolicy } from './policy-definitions/persistent/can-add-comment';
import { canDeleteCommentPolicy } from './policy-definitions/persistent/can-delete-comment';
import { canDownloadAttachmentPolicy } from './policy-definitions/persistent/can-download-attachment';
import { canExport } from './policy-definitions/persistent/can-export';
import { canReadPolicy } from './policy-definitions/persistent/can-read';
import { canWritePolicy } from './policy-definitions/persistent/can-write';
import {
  isAllowance,
  isDisallowance,
  type TransientPolicy,
  type Policy,
  type PolicyAction,
  type PersistentPolicy,
} from './policy-manager-types';

/**
 * Default list of `persistent` policies, which CANNOT be removed during the
 * runtime of the application.
 */
const persistentPolicies: PersistentPolicy[] = [
  canWritePolicy,
  canReadPolicy,
  canAddCommentPolicy,
  canDeleteCommentPolicy,
  canExport,
  canDownloadAttachmentPolicy,
];

/**
 * A class responsible for managing policies enabled in the application, and
 * enforcing them. You may add `transient` policies during runtime.
 */
export class PolicyManager {
  /**
   * Currently active list of policies. These are used on every call to
   * {@link can} to evaluate whether the current user can perform an action.
   *
   * To inject transient policies during runtime, see {@link addPolicies} and
   * {@link removePolicies} for removing them.
   */
  #policies: Policy[] = [...persistentPolicies];
  #subscribers: VoidFunction[] = [];

  /**
   * Add a set of transient policies associated with a namespace. Transient
   * policies enable or disable an action after the application has already started.
   *
   * @example `.addPolicies('sessionFull', [cannotWrite]) // disable writing to
   * the document after the session has become full.
   *
   * @param namespace used to identify a set of policies, and remove them (see
   * {@link removePolicies})
   * @param policies the list of transient policies to add
   */
  addPolicies(namespace: string, policies: TransientPolicy[]) {
    for (const policy of policies) {
      this.addPolicy(namespace, policy);
    }

    this.#subscribers.forEach((subscriber) => subscriber());
  }

  /**
   * Remove all transient policies associated with a namespace.
   * @param namespace
   */
  removePolicies(namespace: string) {
    for (let idx = this.#policies.length - 1; idx > 0; idx--) {
      const policy = this.#policies[idx];
      this.removePolicy(namespace, policy.id);
    }

    this.#subscribers.forEach((subscriber) => subscriber());
  }

  /**
   * Summarises currently active policies into a `|` delimited string. Used for
   * observability purposes (e.g. Sentry).
   * @returns summarised policy summary
   */
  summarise() {
    const active: string[] = [];

    for (const policy of this.#policies) {
      active.push(policy.id);
    }

    return active.join('|');
  }

  /**
   * Summarises currently active policies into a `|` delimited string. Used for
   * observability purposes (e.g. Sentry).
   * @returns summarised policy summary
   */
  detailedSummarise() {
    const active: string[] = [];

    for (const policy of this.#policies) {
      active.push(`id: ${policy.id}, type: ${policy.type}, tag: ${policy.tag}`);
    }

    return active.join('|');
  }

  /**
   * Checks if an action can be performed, based upon the current set of policies.
   *
   * @description Evaluates every current policy in {@link policies}
   * @description If any policy says an action CANNOT be performed, we return `false`
   * @description Otherwise, only if a policy explicitly allows an action, we return `true`,
   * provided the above is not `true` (that a policy a has not disallowed the action)
   * @description To enable or disable actions transiently after the application
   * has started, see {@link addPolicies} and {@link removePolicies}.
   *
   * @param action the action wishing to be performed
   * @param state the current claims a user has
   *
   * @returns <boolean> whether a user can perform an action
   */
  can(action: PolicyAction, state: Set<PolicyAction>) {
    const outcomes = this.#policies.map((p) => p.rule(state));

    const allowance = outcomes.find(
      (outcome) => isAllowance(outcome) && outcome.can === action,
    );
    const disallowance = outcomes.find(
      (outcome) => isDisallowance(outcome) && outcome.cannot === action,
    );

    // always favour disallowances - they CANNOT be overridden
    if (disallowance) {
      return false;
    }

    // only allow an action if it is explicitly not disallowed
    if (allowance && !disallowance) {
      return true;
    }

    return false;
  }

  /**
   * The inverse of {@link can} for API convenience purposes.
   * @param action action wishing to be performed
   * @param state the current claims a user has
   * @returns <boolean> whether a user cannot perform an action
   */
  cannot(action: PolicyAction, state: Set<PolicyAction>) {
    return !this.can(action, state);
  }

  /**
   * Subscribes to when current policies change (on addition or removal).
   * @param cb
   * @returns <fn> unsubscribe callback
   */
  subscribe(cb: VoidFunction) {
    this.#subscribers.push(cb);

    return () => this.unsubscribe(cb);
  }

  /**
   * Checks if a policy is currently active, by namespace.
   * @param policy
   * @returns
   */
  hasNamespace(policy: string) {
    return this.#policies.some((p) => p.tag === policy);
  }

  /**
   * Unsubscribe a listener from changes in policies.
   * @param cb
   * @returns
   */
  unsubscribe(cb: VoidFunction) {
    const idx = this.#subscribers.findIndex((s) => s === cb);
    if (idx === -1) {
      return;
    }

    this.#subscribers.splice(idx, 1);
  }

  /**
   * Clears all local state, including subscribers.
   */
  destroy() {
    this.#policies = [...persistentPolicies];
    this.#subscribers.length = 0;
  }

  private addPolicy(tag: string, policy: Policy) {
    this.#policies.push({ ...policy, tag });
  }

  private removePolicy(tag: string, id: string) {
    const idx = this.#policies.findIndex((p) => p.id === id && p.tag === tag);

    if (idx === -1) {
      return;
    }

    const policy = this.#policies[idx];
    if (policy.type === 'persistent') {
      return;
    }

    this.#policies.splice(idx, 1);
  }
}
